/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    ServiceEndpoint,
    ServiceGlobalEndpointSearchRequest,
} from 'client-code/sls-service-endpoints';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { ISlsServiceGlobalEndpointsServiceRepository } from '../interfaces/ISlsServiceGlobalEnpointsServiceRepository';
import { Log } from '../logging/Log';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class SlsServiceGlobalEndpointsServiceRepositoryImpl
    implements ISlsServiceGlobalEndpointsServiceRepository
{
    TAG: string = 'SlsServiceGlobalEndpointsServiceRepositoryImpl';

    private mSlsEndpointsApi: DefaultApi;

    private static mServiceEndpoints: { key?: [value: string] } = {};

    constructor(baseUrl: string) {
        const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;

        const configurationParameters: ConfigurationParameters = {
            basePath: baseUrl,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };

        const configuration = new Configuration(configurationParameters);
        this.mSlsEndpointsApi = new DefaultApi(configuration);
    }

    private async getSlsGlobalEndpoints(): Promise<ServiceEndpoint[]> {
        try {
            const requestParameters: ServiceGlobalEndpointSearchRequest = {
                applicationName: DeviceModel.getAppName(),
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
            };

            const slsServiceLoginEndpointSearchResponse =
                await this.mSlsEndpointsApi.serviceGlobalEndpointSearch(requestParameters);

            slsServiceLoginEndpointSearchResponse.data?.serviceEndpoints?.forEach(
                (serviceEndpoint: ServiceEndpoint) => {
                    let baseUrl = `http://${serviceEndpoint.hostname}:${serviceEndpoint.port}`;
                    if (serviceEndpoint.port === 443) {
                        baseUrl = baseUrl.replace('http://', 'https://');
                    }
                    SlsServiceGlobalEndpointsServiceRepositoryImpl.mServiceEndpoints[
                        serviceEndpoint.service
                    ] = baseUrl;
                },
            );

            return slsServiceLoginEndpointSearchResponse.data.serviceEndpoints || [];
        } catch (error) {
            Log.i(this.TAG, 'getSlsLoginEndpoints', error);
            throw error;
        }
    }

    async fetchSlsGlobalEndpoints(): Promise<ServiceEndpoint[]> {
        try {
            return await this.getSlsGlobalEndpoints();
        } catch (error) {
            Log.e(this.TAG, 'fetchSlsGlobalEndpoints', error);
            throw error;
        }
    }

    static getServiceEndpointByServiceName(serviceName: string): string {
        const serviceBaseUrl =
            SlsServiceGlobalEndpointsServiceRepositoryImpl.mServiceEndpoints[serviceName];
        if (!serviceBaseUrl) throw Error(`no entry for ${serviceName} in slsGlobalEndpoints`);
        return serviceBaseUrl;
    }
}
